import React from "react";
import Table from "../../../components/Table/Table";
import { PaginationContainer, Header } from "../HomeStyled";
import Pagination from "../../../components/Pagination/Pagination";
import {
  fileNameColumn,
  assignedOnColumn,
  notesColumn,
  renderPendingWithText,
  renderSharedStatus,
} from "../../../utils/columns";
import PropTypes from "prop-types";
import WorkflowAvatars from "./WorkflowAvatars";
import ReminderButton from "../../../components/common/ReminderButton";
import { commonProps, paginationShape, signatureItemShape } from "../Proptypes/Proptypes";
import MatterLoader from "../../../components/Loader/MatterLoader";
import { IconButton, Tooltip } from "@mui/material";
import { XCircle } from "@phosphor-icons/react";

// Nobody is left to remind on these: the request is over, one way or
// another. (Matches the backend's closed set plus COMPLETED.)
const CLOSED_REQUEST_STATUSES = [
  "APPROVED", "REWORK", "COMPLETED",
  "CANCELLED", "EXPIRED", "DECLINED", "FAILED",
];

const SENT_SCOPE_OPTIONS = [
  { value: "mine", label: "Sent by me" },
  { value: "team", label: "Everyone" },
];

const OthersSignature = ({
  sentForSignatureList,
  currentPage = 1,
  handlePageChange,
  onSort,
  getRowProps,
  handleBellClick,
  handleCancelClick,
  filterState,
  handleChangePageSize,
  isLoading = false,
  isInitialLoad = false,
}) => {
  const { sentForSignature } = filterState;
  const currentScope = sentForSignature?.scope === "team" ? "team" : "mine";
  const search = "search logic has to be added here";
  const sentByColumn = {
    title: <Header>SENT BY</Header>,
    dataIndex: "sent_by",
    key: "sent_by",
  };
  const columns = [
    fileNameColumn(search, onSort),
    ...(currentScope === "team" ? [sentByColumn] : []),
    assignedOnColumn,
    {
      title: <Header>PENDING WITH</Header>,
      dataIndex: "pending_with",
      key: "pending_with",
      render: renderPendingWithText,
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (_, record) => renderSharedStatus(record, "ESign"),
    },
    {
      title: <Header>ORDER OF WORKFLOW</Header>,
      dataIndex: "workflow_visualization",
      key: "workflow_visualization",
      render: (value) => {
        const data = Array.isArray(value) ? value : [];
        const sortedData = [...data].sort(
          (a, b) => a.order_index - b.order_index
        );
        return <WorkflowAvatars values={sortedData} />;
      },
    },
    notesColumn(search),
    {
      title: <Header>ACTIONS</Header>,
      dataIndex: "",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <ReminderButton
            record={record}
            handleBellClick={handleBellClick}
            canRemindField="workflow_visualization"
            disabledStatuses={CLOSED_REQUEST_STATUSES}
          />
          {(record.can_cancel ?? record.status === "PENDING") && handleCancelClick && (
            <Tooltip title="Cancel signature request" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelClick(record);
                }}
                sx={{ color: "#ef4444", "&:hover": { background: "#fef2f2" } }}
              >
                <XCircle size={18} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ position: 'relative', minHeight: '400px' }}>
      {isLoading && !isInitialLoad && (
        <MatterLoader message="Loading..." offsetTop={0} absolute={true} />
      )}
      <div
        role="tablist"
        aria-label="Whose signature requests"
        style={{ display: "flex", gap: 8, marginBottom: 12 }}
      >
        {SENT_SCOPE_OPTIONS.map(({ value, label }) => (
          <button
            type="button"
            key={value}
            role="tab"
            aria-selected={currentScope === value}
            onClick={() => value !== currentScope && handlePageChange({ label: "scope", value })}
            style={{
              border: 0,
              borderRadius: 16,
              cursor: "pointer",
              padding: "6px 12px",
              background: currentScope === value ? "#1D4ED8" : "#E5E7EB",
              color: currentScope === value ? "#FFFFFF" : "#1F2937",
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {(!isLoading || isInitialLoad) && (
        <>
          <Table
            columns={columns}
            data={sentForSignatureList?.results || []}
            onRow={getRowProps}
            emptyMessage={"No records found"}
          />
          {sentForSignatureList?.results?.length > 0 && (
            <PaginationContainer>
              <Pagination
                total={sentForSignatureList?.pagination?.count}
                current={currentPage}
                pageSize={sentForSignature?.page_size}
                onChange={(page) =>
                  handlePageChange({ label: "page", value: page })
                }
                adjustable={false}
                onPageSizeChange={handleChangePageSize}
              />
            </PaginationContainer>
          )}
        </>
      )}
    </div>
  );
};

OthersSignature.propTypes = {
  sentForSignatureList: PropTypes.shape({
    results: PropTypes.arrayOf(signatureItemShape),
    pagination: paginationShape,
  }),
  handleBellClick: PropTypes.func.isRequired,
  handleCancelClick: PropTypes.func,
  ...commonProps,
  currentPage: PropTypes.number,
};

OthersSignature.defaultProps = {
  currentPage: 1,
};

export default OthersSignature;
